import { Sidebar } from '../components/Sidebar';
import {Route, Routes, useOutletContext} from 'react-router-dom';
import WaitingRoom from './Staff/WaitingRoom/WaitingRoom';
import { StudentDetailPage } from './Staff/StudentDetails/StudentDetail';
import {NotAuthorizedComponent} from "../components/NotAuthorized/NotAuthorizedComponent";
import {AuthContext } from "../components/AuthWrapper";
import Staff from './Staff/Admin/Staff';
import Dashboard from './Staff/StudentTable/Dashboard';
import PageTitle from '../components/PageTitle';
import { PageNotFound } from '../components/PageNotFound';
import { useEffect, useMemo, useState } from 'react';
import { DataForAdvisor } from '../api/client';
import toast from 'react-hot-toast';
import { localstorageKeyWaitingRoomIds } from '../components/LocalStorageProcesser';
import AwardsAndProgramsManager from './Staff/AwardsPrograms/AwardsProgramsManager';

// How many minutes it takes before we attempt a refresh of the waiting room list
const UPDATERATE = 1

export function AdminRoutes(): JSX.Element {

  const {state, client, loggedIn }: AuthContext = useOutletContext();
  const [currentWaitingLine, setCurrentWaitingLine] = useState<DataForAdvisor[]>([]);
  const [seenAppointmentIds, setSeenAppointmentIds] = useState<Set<string>>(() => {
    // Grab localstorage data (if we have any) of seen appointments, then parse into set so we can easily check the data later
    const stored = localStorage.getItem(localstorageKeyWaitingRoomIds);

    if (!stored) return new Set();

    const parsed: string[] = JSON.parse(stored);
    return new Set(parsed);
  })

  const refreshSeenWaitingLine = (line: DataForAdvisor[]) => {
    // create a new empty set
    const updated: Set<string> = new Set();
    for (const appointment of line) {
      // if ID is not undefined (which shouldn't ever be the case I think, but TS yells otherwise), add it to our set of IDs
      if (appointment.appointmentId) {
        updated.add(appointment.appointmentId)
      }
    }

    setSeenAppointmentIds(updated);
  };

  const fetchWaitingStudents = async () => {

    // Wait for user and admin state to load, otherwise the backend will throw authentication errors
    if (!state.user || !state.user.admin) return;

    try {
      let result = await client?.admin_GetWaitingStudents();

      // Set waiting line to list of waiting students
      setCurrentWaitingLine(result ?? []);
    } catch (error) {
      toast.error(`Failed to fetch waiting students: ${error}`);
      setCurrentWaitingLine([]);
    }
  };

  // Load data on mount, then refresh every 1000ms (1 second) * 60 (so total 60 seconds)
  useEffect(() => {
    fetchWaitingStudents();
    const refresh = setInterval(() => {
      fetchWaitingStudents();
    }, 1000 * 60 * UPDATERATE)

    return () => {
      clearInterval(refresh);
    }

  }, [client, state]);

const newWaitingAmount = useMemo(() => {
  // we want to reduce the array to a count of how many different appointments than we last saw
  return currentWaitingLine.reduce((count, appointment) => {
    // if ID is undefined (again shouldn't ever happen, but TS yells otherwise), dont iterate the count
    if (!appointment.appointmentId) return count;
    // If the set of IDs does not include the current appointment, iterate our difference count
    return seenAppointmentIds.has(appointment.appointmentId) ? count : count + 1;
  }, 0);
}, [currentWaitingLine, seenAppointmentIds])

useEffect(() => {
  // Change set to array to be able to correctly stringify it into JSON for localstorage
  const appointmentIDarray = Array.from(seenAppointmentIds);

  // add the seen appointments to localstorage so we dont show a notification even if we refresh!
  localStorage.setItem(
    localstorageKeyWaitingRoomIds,
    JSON.stringify(appointmentIDarray)
  );
}, [seenAppointmentIds])

  return (
      <div>
        <PageTitle title="Admin" />
        {(!state.user || !state.user.admin) ? (
            <NotAuthorizedComponent state={state} loggedIn={loggedIn} />
          ) :
        <div className='flex'>
          <Sidebar waitingAmount={newWaitingAmount}/>
          <div className="min-h-[80vh] flex justify-center w-full">
            <Routes>
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="staff" element={<Staff />} />
              <Route path="waitingRoom" element={<WaitingRoom currentLine={currentWaitingLine} fetchWaitingStudents={fetchWaitingStudents} setLastSeenWaitingLine={refreshSeenWaitingLine}/>} />
              <Route path="studentDetail/:usfid" element={<StudentDetailPage />} />
              <Route path="awardsprograms" element={<AwardsAndProgramsManager state={state} client={client}/>}/>
              <Route path="*" element={<PageNotFound />} />
            </Routes>
          </div>
        </div>}
      </div>
  );
}
